import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { use100vh } from 'react-div-100vh'
import {
  Box,
  ButtonUnstyled,
  Icon,
  P,
  Button,
  LoadingSpinner,
  ScreenReaderText,
  Collapse,
} from '@allied-solutions/affinity'
import { LinkAnchor } from './LinkAnchor'
import { useCiamAuthorize } from '../hooks/useCiamAuthorize'
import { Link } from 'gatsby'
import { theme } from '../theme'
import { Logo } from './Logo'
import {
  Menu,
  Close01,
  Home01,
  QuestionCircle,
  Rocket01,
  UserSolid,
} from '@allied-solutions/affinity-react-icons/dist/standard'

const StyledButton = styled(Button)`
  width: 100%;
  margin-bottom: ${props => props.theme.space['2xl']};
`

export const MobileMenuWithHeader = ({ needMenu, setNeedMenu, menuHeight }) => {
  const { handleAuthorize, isAuthorizing } = useCiamAuthorize()
  const menuTriggerRef = useRef(null)
  const viewportHeight = use100vh()
  const maxMenuHeightPx =
    typeof menuHeight === 'number'
      ? menuHeight
      : Math.max(320, viewportHeight - 48)

  useEffect(() => {
    document.body.style.overflow = needMenu ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [needMenu])

  return (
    <>
      <Box display={needMenu ? 'none' : 'contents'}>
        <ButtonUnstyled
          ref={menuTriggerRef}
          id="homeSupportMenuToggle"
          fontSize="1"
          onClick={() => setNeedMenu(true)}
        >
          <Box display="flex" alignItems="center" gap="2" color="text.default">
            <Icon ml="2" src={Menu} />
            <P typeStyle="buttonLarge" mb="0">
              Menu
            </P>
          </Box>
        </ButtonUnstyled>
      </Box>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex="dialog"
        css={`
          pointer-events: ${needMenu ? 'auto' : 'none'};
        `}
      >
        <Collapse
          id="Global__Header__Collapse--guestMenu"
          isOpen={needMenu}
          triggerRef={menuTriggerRef}
        >
          <Box pb="6xl" pt={0}>
            <Box
              width="100%"
              pt={6}
              pb={16}
              bg="white"
              overflow="auto"
              maxHeight={`${maxMenuHeightPx}px`}
              borderRadius={theme.radii.xl}
              boxShadow="xl"
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid"
                borderColor="mutedLine"
                px={8}
                pb={8}
              >
                <Logo />
                <ButtonUnstyled
                  onClick={() => setNeedMenu(false)}
                  id="homeSupportMenuToggleClose"
                  aria-label="close menu"
                >
                  <Box
                    width="8"
                    height="8"
                    borderRadius="8"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gradient="blue"
                  >
                    <Icon src={Close01} color="white" />
                  </Box>
                </ButtonUnstyled>
              </Box>
              <Box px={8}>
                <P typeStyle="titleLarge.bold" textAlign="center" mt="5xl">
                  We are here to help.
                </P>
                <P typeStyle="bodyLarge" textAlign="center">
                  Did you receive a letter or email asking to confirm your
                  insurance? You're in the right place! We work with you and
                  your lender to confirm you have the right coverage. The
                  process only takes a few minutes.
                </P>
                <LinkAnchor
                  to="/"
                  id="Global__Header__Link--home"
                  onClick={() => setNeedMenu(false)}
                >
                  <StyledButton
                    id="home-button"
                    icon={Home01}
                    iconPos="left"
                    outlined
                    variant="gray"
                    size="lg"
                  >
                    Home
                  </StyledButton>
                </LinkAnchor>
                <LinkAnchor
                  to="/support/"
                  id="Global__Header__Link--support"
                  onClick={() => setNeedMenu(false)}
                >
                  <StyledButton
                    id="home-support-button"
                    icon={QuestionCircle}
                    iconPos="left"
                    outlined
                    variant="gray"
                    size="lg"
                  >
                    Support
                  </StyledButton>
                </LinkAnchor>
                <LinkAnchor
                  id="Global__Header__Button--enterReferenceId"
                  forwardedAs={Link}
                  to="/enter-reference-id/"
                  onClick={() => setNeedMenu(false)}
                >
                  <StyledButton
                    id="home-start-verification-button"
                    icon={Rocket01}
                    iconPos="left"
                    outlined
                    variant="brand"
                    size="lg"
                  >
                    Start Verification
                  </StyledButton>
                </LinkAnchor>
                <Button
                  id="Global__Header__Button--Login"
                  boxShadow="none"
                  display="flex"
                  minWidth="100%"
                  size="lg"
                  icon={UserSolid}
                  alignItems="center"
                  onClick={handleAuthorize}
                >
                  {isAuthorizing ? (
                    <Box as="span" display="inline-flex" alignItems="center">
                      <LoadingSpinner size="sm" aria-hidden />
                      <ScreenReaderText>Logging in…</ScreenReaderText>
                    </Box>
                  ) : (
                    'Log In'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </>
  )
}
